<template>
  <div class="base-button">
    <button
      :class="[{block: props.block}, {'px-3': !props.disableInnerPadding}, {'mx-2': !props.disableOuterPadding}]"
      v-ripple
      :disabled="props.disabled"
      :style="[style, hoverStyle]"
      @mouseover="handleMouseOver"
      @mouseleave="handleMouseLeave"
      @click="!props.loading && emit('click')">
      <spinner-loader v-if="props.loading" :size="props.smallLoading ? 'sm' : 'md'" :color="props.outlined? props.color: 'white'"/>
      <flex-layout v-else align="center" class="custom-border" justify="center">
        <base-icon :class="iconPadding ? 'mb-1 me-1' : ''" v-if="props.icon" :name="props.icon" justify="center" align="center" :fill="textColor" :height="iconSizes[props.iconHeight]" :width="iconSizes[props.iconWidth]" />
        <base-text class="child-container" :color="textColor" :size="props.textSize" justify="center" :bold="props.bold">
          <slot/>
        </base-text>
      </flex-layout>
    </button>
  </div>
</template>

<script lang="ts" setup>

import {defineProps, defineEmits, computed, ref} from 'vue';
import { Color } from '@/store/theme.store';
import { useTheme } from '@/composable/ui/useTheme';
import SpinnerLoader from '@/components/Loaders/SpinnerLoader.vue';
import BaseIcon from '@/components/Images/BaseIcon.vue';
import BaseText from '@/components/Texts/BaseText.vue';
import FlexLayout from '@/components/Layouts/FlexLayout.vue';

const theme = useTheme();

const props = defineProps({
  size: {
    type: String as() => 'xs' |'sm' | 'smMd' | 'md' | 'lg',
    default: 'md',
  },
  minWidth: {
    type: Number,
    required: false,
  },
  color: {
    type: String as() => Color,
    default: 'primary',
  },
  block: Boolean,
  border: {
    type: Boolean,
    default: true,
  },
  outlined: Boolean,
  disabled: Boolean,
  onlyText: Boolean,
  loading: Boolean,
  bold: Boolean,
  customSize: {
    type: Number,
    default: 0,
  },
  icon: String,
  textSize: {
    type: String as() => 'xs' |'sm' | 'md' | 'lg',
    default: 'md',
  },
  borderColor: {
    type: String as () => Color,
  },
  outlinedBlockBorder: {
    type: Boolean,
    default: false,
  },
  outlinedBlockBorderColor: {
    type: String as () => Color,
    default: 'primary',
  },
  outlinedBlockBorderOffset: {
    type: Number,
    default: 0,
  },
  outlinedBlockBorderSize: {
    type: String,
    default: '2',
  },
  textColor: {
    type: String as () => Color,
  },
  buttonDisabledColor: {
    type: String as () => Color,
    default: "grayDarker"
  },
  iconWidth: {
    type: String as() => 'xs' |'sm' | 'md' | 'lg',
    default: 'md',
  },
  iconHeight: {
    type: String as() => 'xs' |'sm' | 'md' | 'lg',
    default: 'md',
  },
  iconPadding: {
    type: Boolean,
    default: false,
  },
  disableInnerPadding: {
    type: Boolean,
    default: false,
  },
  disableOuterPadding: {
    type: Boolean,
    default: false,
  },
  customBorderRadius: {
    type: String,
    required: false,
  },
  disableEffects: {
    type: Boolean,
    default: false,
  },
  customShadow: {
    type: Boolean,
    default: false,
  },
  smallLoading: {
    type: Boolean,
    default: false,
  }
});
const emit = defineEmits(['click']);

const heights = {
  xs: 24,
  sm: 40,
  smMd: 56,
  md: 56,
  lg: 64,
};
const widths = {
  xs: 80,
  sm: 120,
  smMd: 160,
  md: 200,
  lg: 200,
};

const iconSizes = {
  xs: '12px',
  sm: '18px',
  md: '24px',
  lg: '30px',
};

const color = computed(() => theme.resolveColor(props.color));

const style = computed(() => {
  let backgroundColor = color.value;
  let borderColor = 'transparent';
  let textColor = theme.contrastColor(backgroundColor);
  if(props.outlined) {
    backgroundColor = theme.resolveColor('offWhite');
    borderColor = color.value;
    textColor = color.value;
  } else {
    if(props.color) backgroundColor = theme.resolveColor(props.color);
    if(props.borderColor) borderColor = theme.resolveColor(props.borderColor);
    if(props.textColor) textColor = theme.resolveColor(props.textColor);
  }

  return {
    backgroundColor: props.disabled ? theme.resolveColor(props.buttonDisabledColor) : backgroundColor,
    borderColor: borderColor,
    borderWidth: '1px',
    borderRadius: props.customBorderRadius ? props.customBorderRadius : props.border ? '5px' : 0,
    color: textColor,
    borderStyle: 'solid',
    minWidth: props.minWidth ? `${props.minWidth}px` :  props.block ? 'initial' : `${widths[props.size]}px`,
    height: props.customSize ? `${props.customSize}px` : `${heights[props.size]}px`,
    cursor: props.loading ? 'wait' : 'pointer',
    fontWeight: props.bold ? 700 : 400,
    outline: props.outlinedBlockBorder ? `${(props.outlinedBlockBorderSize || '2')}px solid ${theme.resolveColor(props.outlinedBlockBorderColor || 'primary')}` : 'none',
    outlineOffset: props.outlinedBlockBorderOffset ? `${props.outlinedBlockBorderOffset}px` : '0px',
    boxShadow: props.customShadow ? '10px 10px 10px #00000017' : props.disableEffects ? '' : '0 5px 10px #00000017',
    transition: props.disableEffects ? '' : '0.3s ease-in-out',
  }
});

const isHovered = ref(false);

const hoverStyle = computed(() => {
  return {
    boxShadow: isHovered.value && !props.disableEffects  ? 'none' : style.value.boxShadow,
  };
});

function handleMouseOver() {
  if (!props.disableEffects) isHovered.value = true;
}

function handleMouseLeave() {
  isHovered.value = false;
}

const textColor = computed(() => {
  let colorText: Color = 'primary';
  if (props.color === 'primary') {
    colorText = 'white';
  }
  if (props.outlined) {
    colorText = 'primary';
  }
  return colorText;
});
</script>

<style scoped>

button:hover {
  background-color: var(--primaryDark);
  transform: scale(0.99);
  box-shadow: none;
}

button[disabled] {
  cursor: not-allowed !important;
}
.block {
  width: calc(100% - 36px);
}

.child-container {
  position: relative;
}

</style>
